import { useContext } from 'react';
import LanguageContext from '@/contexts/LanguageContext';
import id from '@/locales/id';
import en from '@/locales/en';

const translations = { id, en };

/**
 * Hook for accessing translations
 * @returns {Object} translation object and helper function
 */
export function useTranslation() {
  const context = useContext(LanguageContext);
  
  if (!context) {
    // Fallback to Indonesian if context not available
    return {
      t: (key) => getNestedValue(id, key),
      language: 'id',
      isEnglish: false,
      isIndonesian: true
    };
  }
  
  const { language, isEnglish, isIndonesian } = context;
  const currentTranslations = translations[language] || translations.id;
  
  /**
   * Get translation by dot notation key
   * @param {string} key - Dot notation key (e.g., 'common.calculate')
   * @param {Object} params - Optional parameters for interpolation
   * @returns {string} Translated string
   */
  const t = (key, params = {}) => {
    let value = getNestedValue(currentTranslations, key);
    
    // Fallback to Indonesian if key not found
    if (value === undefined) {
      value = getNestedValue(translations.id, key);
    }
    
    // Return key if still not found
    if (value === undefined) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    
    // Handle string interpolation
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        value = value.replace(new RegExp(`{${paramKey}}`, 'g'), paramValue);
      });
    }
    
    return value;
  };
  
  return {
    t,
    language,
    isEnglish,
    isIndonesian,
    translations: currentTranslations
  };
}

/**
 * Get nested value from object using dot notation
 * @param {Object} obj - Object to search
 * @param {string} key - Dot notation key
 * @returns {any} Value or undefined
 */
function getNestedValue(obj, key) {
  return key.split('.').reduce((o, k) => (o || {})[k], obj);
}

export default useTranslation;
